import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Globe, MapPin, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import api, { apiError } from "./api";
import { useAuth } from "./QcApp";
import { VendorFormDialog } from "./Vendors";

export const STAGE_LABELS = {
  qc: "With QC",
  commercial: "With Commercial",
  unit_head: "With Unit Head",
  accounts: "With Accounts",
  closed: "Closed",
};

export const STAGE_STYLES = {
  qc: "bg-[#1C3F2B]/[0.07] text-[#1C3F2B]",
  commercial: "bg-[#E5A93D]/15 text-[#7a5b12]",
  unit_head: "bg-[#2b6cb0]/10 text-[#2b6cb0]",
  accounts: "bg-[#6b4fa8]/10 text-[#6b4fa8]",
  closed: "bg-[#1C3F2B] text-[#F9F8F6]",
};

const Chooser = () => (
  <div data-testid="qc-flow-chooser">
    <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#8A9A91]">External Lab Invoicing</p>
    <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">Choose a workflow</h1>
    <div className="mt-8 flex flex-wrap gap-6">
      <Link
        to="/qc/invoicing/export"
        data-testid="qc-flow-export"
        className="group block w-full rounded-2xl border border-[#E8E6E1] bg-white p-6 shadow-[0_8px_32px_rgba(28,63,43,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(28,63,43,0.12)] sm:w-[320px]"
      >
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1C3F2B]/[0.06]">
          <Globe className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <h3 className="mt-5 font-display text-xl font-semibold">International Invoicing</h3>
        <p className="mt-1.5 text-[13px] leading-relaxed text-[#8A9A91]">
          Payments to labs outside India — foreign currency remittance with Form A2, Bank Letter and FEMA declaration.
        </p>
      </Link>
      <Link
        to="/qc/invoicing/domestic"
        data-testid="qc-flow-domestic"
        className="group block w-full rounded-2xl border border-[#E8E6E1] bg-white p-6 shadow-[0_8px_32px_rgba(28,63,43,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(28,63,43,0.12)] sm:w-[320px]"
      >
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1C3F2B]/[0.06]">
          <MapPin className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <h3 className="mt-5 font-display text-xl font-semibold">Domestic Invoicing</h3>
        <p className="mt-1.5 text-[13px] leading-relaxed text-[#8A9A91]">
          Payments to labs within India — TDS deduction and the Bill Passed stamp workflow.
        </p>
      </Link>
    </div>
  </div>
);

export default function FlowList() {
  const { flow } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [filter, setFilter] = useState("all");
  const [newOpen, setNewOpen] = useState(false);
  const [vendorId, setVendorId] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [addVendorOpen, setAddVendorOpen] = useState(false);

  const load = () => {
    api.get("/cases", { params: { flow } }).then((r) => setCases(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (flow) load();
  }, [flow]);

  if (!flow) return <Chooser />;

  const isExport = flow === "export";
  const roles = user.roles || [];
  const myStage = roles.includes("commercial") ? "commercial" : roles.includes("unit_head") ? "unit_head" : roles.includes("accounts") ? "accounts" : "qc";
  const shown = (cases || []).filter((c) =>
    filter === "all" ? true : filter === "closed" ? c.stage === "closed" : c.stage === myStage && c.stage !== "closed"
  );

  const createCase = async () => {
    if (!vendorId) return toast.error("Select a vendor (lab)");
    try {
      const { data } = await api.post("/cases", { flow, vendor_id: vendorId, currency });
      toast.success(`Case ${data.case_no} created — upload the invoice to continue`);
      navigate(`/qc/cases/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid={`qc-flow-list-${flow}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#8A9A91]">External Lab Invoicing</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">
            {isExport ? "International Invoicing" : "Domestic Invoicing"}
          </h1>
          <p className="mt-1 text-sm text-[#8A9A91]">
            {isExport ? "Payments to labs outside India." : "Payments to labs within India."}
          </p>
        </div>
        {(roles.includes("qc") || roles.includes("admin")) && (
          <Button data-testid="qc-new-case" onClick={() => setNewOpen(true)} className="rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
            <Plus className="mr-1.5 h-4 w-4" /> New Case
          </Button>
        )}
      </div>

      <div className="mt-6 flex gap-2">
        {[["all", "All"], ["mine", "Needs my action"], ["closed", "Closed"]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`qc-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              filter === k ? "bg-[#1C3F2B] text-[#F9F8F6]" : "border border-[#E8E6E1] bg-white text-[#8A9A91] hover:text-[#1C3F2B]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-[#E8E6E1] bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Case</TableHead>
              <TableHead>Vendor (Lab)</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="text-right">Net Payable</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-sm text-[#8A9A91]">
                  {cases === null ? "Loading…" : "No cases here yet."}
                </TableCell>
              </TableRow>
            )}
            {shown.map((c) => (
              <TableRow
                key={c.id}
                data-testid={`qc-case-row-${c.case_no}`}
                className="cursor-pointer"
                onClick={() => navigate(`/qc/cases/${c.id}`)}
              >
                <TableCell className="font-medium">{c.case_no}</TableCell>
                <TableCell>{c.vendor_name}</TableCell>
                <TableCell className="text-right">{c.currency} {Number(c.gross).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</TableCell>
                <TableCell className="text-right">{c.currency} {Number(c.net).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</TableCell>
                <TableCell>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${STAGE_STYLES[c.stage]}`}>
                    {STAGE_LABELS[c.stage]}
                  </span>
                </TableCell>
                <TableCell className="text-[#8A9A91]">{new Date(c.created_at).toLocaleDateString("en-IN")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="qc-new-case-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">New {isExport ? "Export" : "Domestic"} Case</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Vendor (Lab)</Label>
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger data-testid="qc-new-case-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name} · {v.country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                data-testid="qc-new-case-add-vendor"
                onClick={() => setAddVendorOpen(true)}
                className="text-xs font-medium text-[#1C3F2B] underline underline-offset-2"
              >
                + Add a new vendor
              </button>
            </div>
            {isExport && (
              <div className="space-y-1.5">
                <Label>Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger data-testid="qc-new-case-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["EUR", "USD", "GBP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button data-testid="qc-new-case-create" onClick={createCase} className="w-full rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
              Create case
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <VendorFormDialog
        open={addVendorOpen}
        onOpenChange={setAddVendorOpen}
        onSaved={(v) => {
          setVendors((prev) => [...prev, v]);
          setVendorId(v.id);
          setAddVendorOpen(false);
        }}
      />
    </div>
  );
}
